import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import { Contrato } from "../../services/api/contratos";
import { useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FileUpload } from "../fileUpload";
import { RequestError } from "../../services";

interface Props extends DialogProps {
  onAdd?: (contrato?: Contrato, file?: File) => void;
}

const tipos: { [key: string]: string; } = {
  '1': "Projetos Privados Únicos",
  "5": "Projetos Guarda-chuva",
  "7": "Licitações",
  "9": "Projetos Internos",
};

const areas: { [key: string]: string; } = {
  "1": "Recursos Hídricos",
  "2": "Saneamento",
  "3": "Geotecnia",
  "4": "Meio Ambiente e Sustentabilidade",
  "5": "Tecnologia e Inovação",
};

export const NewContratoModal: React.FC<Props> = ({ onAdd, ...dialogProps }) => {
  const dateNow = new Date(Date.now());

  const [anchorTipo, setAnchorTipo] = useState<null | HTMLElement>(null);
  const [anchorArea, setAnchorArea] = useState<null | HTMLElement>(null);
  const [files, setFiles] = useState<File[]>();
  const [error, setError] = useState<RequestError>();
  const [tipoProjetoText, setTipoProjetoText] = useState("Tipo do Projeto");
  const [areaProjetoText, setAreaProjetoText] = useState("Área do Projeto");
  const [contrato, setContrato] = useState<Contrato>({
    id: 0,
    nome: "",
    descricao: "",
    contratoLote: "HBR000000000",
    validade: new Date(dateNow.getFullYear() + 2, dateNow.getMonth(), dateNow.getDay()),
    status: true,
    file: "",
    dataCadastro: new Date(Date.now()),
  });

  const handleOpenTipo = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorTipo(event.currentTarget);
  };
  const handleOpenArea = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorArea(event.currentTarget);
  };

  const handleChangeContratoLote = (tipo: string, value: string | number) => {
    if (anchorTipo) setAnchorTipo(null);
    if (anchorArea) setAnchorArea(null);

    const loteN = contrato.contratoLote;
    let newLote = "";
    if (tipo == "tipo") {
      newLote += loteN.substring(0, 3);
      newLote += value;
      newLote += loteN.substring(4);
    } else if (tipo == "area") {
      newLote += loteN.substring(0, 11);
      newLote += value;
    } else if (tipo == "seq") {
      value = Number(value);
      if (value < 0) value = 0;
      if (value > 9999) value = 9999;
      value = value.toFixed(0);
      newLote += loteN.substring(0, 4);
      newLote += value.toString().rjust(4, "0");
      newLote += loteN.substring(8);
    } else if (tipo == "sub") {
      value = Number(value);
      if (value < 0) value = 0;
      if (value > 999) value = 999;
      value = value.toFixed(0);
      newLote += loteN.substring(0, 8);
      newLote += value.toString().rjust(3, "0");
      newLote += loteN.substring(11);
    }

    if (tipo == 'tipo') setTipoProjetoText(tipos[value]);
    if (tipo == 'area') setAreaProjetoText(areas[value]);

    setContrato({ ...contrato, "contratoLote": newLote });
  };

  const handleAdd = () => {
    if (contrato.nome == "") {
      setError({
        error: "Erro",
        message: "O nome do arquivo deve ser adicionado"
      });
      return;
    }
    if (contrato.contratoLote == "") {
      setError({
        error: "Erro",
        message: "O Contrato N°/Lote do arquivo deve ser adicionado"
      });
      return;
    }
    if (contrato.descricao == "") {
      setError({
        error: "Erro",
        message: "A descrição do arquivo deve ser adicionada"
      });
      return;
    }
    if (!files) {
      setError({
        error: "Erro",
        message: "O documento do contrato deve ser adicionado"
      });
      return;
    }
    if (files.length > 1) {
      setError({
        error: "Erro",
        message: "O documento do contrato deve ser único"
      });
      return;
    }

    if (onAdd) onAdd(contrato, files[0]);

    setFiles(undefined);
    setContrato({
      id: 0,
      nome: "",
      contratoLote: "",
      descricao: "",
      idEmpreendimento: "",
      idBarragem: 0,
      validade: new Date(dateNow.getFullYear() + 2, dateNow.getMonth(), dateNow.getDay()),
      status: true,
      file: "",
      dataCadastro: new Date(Date.now()),
    });
  };

  const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setContrato({ ...contrato, [name]: value });
  };

  return <>
    {/* Erro */}
    {error &&
      <Snackbar
        open={error !== null}
        autoHideDuration={4500}
        onClose={() => setError(undefined)}
        message="Note archived"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setError(undefined)} severity="error" sx={{ width: "100%" }}>
          {error.error + ":" + error.message}
        </Alert>
      </Snackbar>
    }

    {/* Menu do tipo do contrato*/}
    <Menu
      id="dropdown-menu"
      anchorEl={anchorTipo}
      open={Boolean(anchorTipo)}
      onClose={() => setAnchorTipo(null)}
      MenuListProps={{
        "aria-labelledby": "dropdown-button",
      }}
    >
      {Object.keys(tipos).map((key: string) => {
        return <MenuItem key={key} onClick={() => handleChangeContratoLote("tipo", key)}>
          {key} - {tipos[key] as string}
        </MenuItem>;
      })}
    </Menu>

    {/* Menu da área do contrato*/}
    <Menu
      id="dropdown-menu"
      anchorEl={anchorArea}
      open={Boolean(anchorArea)}
      onClose={() => setAnchorArea(null)}
      MenuListProps={{
        "aria-labelledby": "dropdown-button",
      }}
    >
      {Object.keys(areas).map((key: string) => {
        return <MenuItem key={key} onClick={() => handleChangeContratoLote("area", key)}>
          {key} - {areas[key] as string}
        </MenuItem>;
      })}
    </Menu>

    {/* Dialog */}
    <Dialog {...dialogProps}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {`Novo Contrato - ${contrato.contratoLote}`}
      </DialogTitle>

      <DialogContent>
        <Box sx={{
          display: "flex",
          flexDirection: 'row',
        }}>
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Lote e Nome */}
            <Box sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}>
              {/* Tipo e Área */}
              <Box sx={{
                width: '176px',
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}>
                {/* Botão Tipo */}
                <Button
                  variant="contained"
                  onClick={handleOpenTipo}
                  aria-controls={anchorTipo ? "dropdown-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorTipo ? "true" : undefined}
                >
                  {tipoProjetoText}
                </Button>
                <div style={{ height: "15px" }} />

                {/* Botão Área */}
                <Button
                  variant="contained"
                  onClick={handleOpenArea}
                  aria-controls={anchorArea ? "dropdown-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorArea ? "true" : undefined}
                >
                  {areaProjetoText}
                </Button>
              </Box>
              <div style={{ width: "15px" }} />

              {/* Sequência e SubcódigoSubcódigo */}
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}>
                {/* Sequência */}
                <TextField
                  fullWidth
                  required
                  label="Sequência"
                  name="seq"
                  value={contrato.contratoLote.substring(4, 8)}
                  onChange={(event) => handleChangeContratoLote("seq", event.target.value)}
                  variant="outlined"
                />
                <div style={{ height: "15px" }} />

                {/* Subcódigo */}
                <TextField
                  fullWidth
                  required
                  label="Subcódigo"
                  name="seq"
                  value={contrato.contratoLote.substring(8, 11)}
                  onChange={(event) => handleChangeContratoLote("sub", event.target.value)}
                  variant="outlined"
                />
                <div style={{ height: "15px" }} />
              </Box>
              <div style={{ width: "15px" }} />
            </Box>
            <div style={{ height: "15px", flexShrink: 0 }} />

            {/* Nome */}
            <TextField
              fullWidth
              required
              label="Nome"
              name="nome"
              value={contrato.nome}
              onChange={handleChangeText}
              variant="outlined"
            />
            <div style={{ height: "15px", flexShrink: 0 }} />

            {/* Descrição e Validade */}
            <Box sx={{
              display: "flex",
              flexDirection: "row",
              flex: 1,
            }}>
              <TextField
                fullWidth
                required
                multiline
                label="Descrição"
                name="descricao"
                value={contrato.descricao}
                onChange={handleChangeText}
                variant="outlined"
              />

              <div style={{ width: "15px" }} />

              <DatePicker
                label="Validade do Contrato"
                value={dayjs(contrato.validade)}
                onChange={(date) => setContrato({
                  ...contrato,
                  validade: date?.toDate() ?? new Date(Date.now())
                })}
              />
            </Box>
          </Box>

          <div style={{ width: "15px" }} />

          <FileUpload
            files={files}
            accept={{
              'application/pdf': ['.pdf']
            }}
            onAddFile={setFiles}
            sx={{
              height: "350px",
              minWidth: "356px",
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          autoFocus
          variant="contained"
          onClick={
            () => dialogProps.onClose ?
              dialogProps.onClose({}, "backdropClick") :
              undefined
          }
        >
          Cancelar
        </Button>
        <Button onClick={handleAdd}>
          Adicionar
        </Button>
      </DialogActions>
    </Dialog>
  </>;
};